import React, { useEffect, useRef, useState } from "react";
import { createGame, tick, setCannonX, getCanvasSize } from "./engine.js";
import type { ParatrooperGame } from "./types.js";

function draw(ctx: CanvasRenderingContext2D, g: ParatrooperGame, groundY: number, cannonY: number) {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  ctx.clearRect(0, 0, w, h);

  ctx.fillStyle = "#00141f";
  ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = "#0a3d0a";
  ctx.fillRect(0, groundY, w, h - groundY);

  ctx.fillStyle = "#1a6b1a";
  ctx.fillRect(0, groundY, w, 4);

  for (const p of g.planes) {
    if (!p.alive) {
      const t = p.explosionTimer / 60;
      const r = 20 + (1 - t) * 30;
      const alpha = t;
      ctx.fillStyle = `rgba(255, 150, 30, ${alpha})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y + 5, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 60, 10, ${alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y + 5, r * 0.6, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    if (p.isHeli) {
      const wobble = Math.sin(Date.now() * 0.006) * 3;
      ctx.fillStyle = "#4a6a2a";
      ctx.fillRect(p.x - 16, p.y + wobble - 3, 32, 7);
      ctx.fillRect(p.x - 3, p.y + wobble - 8, 6, 10);

      ctx.strokeStyle = "rgba(200,200,200,0.6)";
      ctx.lineWidth = 2;
      const rotorAngle = Date.now() * 0.02;
      ctx.beginPath();
      ctx.moveTo(p.x + Math.cos(rotorAngle) * 24, p.y + wobble - 10 + Math.sin(rotorAngle) * 6);
      ctx.lineTo(p.x - Math.cos(rotorAngle) * 24, p.y + wobble - 10 - Math.sin(rotorAngle) * 6);
      ctx.stroke();

      ctx.fillStyle = "#6a9a4a";
      ctx.beginPath();
      ctx.arc(p.x, p.y + wobble + 3, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#8ac86a";
      ctx.beginPath();
      ctx.arc(p.x - 2, p.y + wobble, 3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = "#555";
      ctx.fillRect(p.x - 30, p.y, 60, 10);
      ctx.fillRect(p.x - 5, p.y - 8, 10, 8);

      ctx.fillStyle = "#888";
      ctx.beginPath();
      ctx.moveTo(p.x - 30, p.y);
      ctx.lineTo(p.x - 40, p.y + 10);
      ctx.lineTo(p.x - 25, p.y + 10);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(p.x + 30, p.y);
      ctx.lineTo(p.x + 40, p.y + 10);
      ctx.lineTo(p.x + 25, p.y + 10);
      ctx.fill();

      ctx.fillStyle = "#bada55";
      ctx.beginPath();
      ctx.arc(p.x, p.y + 10, 8, 0, Math.PI);
      ctx.fill();
    }
  }

  for (const e of g.explosions) {
    const t = e.timer / 60;
    const r = 15 + (1 - t) * 20;
    const alpha = t;
    ctx.fillStyle = `rgba(255, 200, 50, ${alpha})`;
    ctx.beginPath();
    ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(255, 80, 20, ${alpha * 0.6})`;
    ctx.beginPath();
    ctx.arc(e.x, e.y, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  for (const t of g.troopers) {
    if (!t.alive) continue;
    if (t.parachute) {
      ctx.strokeStyle = "#ccc";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(t.x, t.y);
      ctx.lineTo(t.x - 12, t.y - 20);
      ctx.moveTo(t.x, t.y);
      ctx.lineTo(t.x + 12, t.y - 20);
      ctx.stroke();

      ctx.fillStyle = "rgba(200,200,200,0.7)";
      ctx.beginPath();
      ctx.ellipse(t.x, t.y - 22, 16, 8, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#2c3e50";
      ctx.beginPath();
      ctx.arc(t.x, t.y, 6, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "#e74c3c";
      ctx.beginPath();
      ctx.arc(t.x, t.y - 2, 3, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = "#444";
      ctx.fillRect(t.x - 5, t.y - 5, 10, 10);

      ctx.fillStyle = "#ff6600";
      ctx.beginPath();
      ctx.arc(t.x, t.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  for (const b of g.bullets) {
    ctx.fillStyle = "#ff0";
    ctx.beginPath();
    ctx.arc(b.x, b.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = "#666";
  ctx.fillRect(g.cannonX - 10, cannonY - 20, 20, 20);

  ctx.fillStyle = "#888";
  ctx.fillRect(g.cannonX - 4, cannonY - 28, 8, 12);

  ctx.fillStyle = "#fff";
  ctx.font = "16px monospace";
  ctx.fillText(`Score: ${g.score}`, 10, 24);
  ctx.fillText(`Landed: ${g.trooperLands}/${g.maxLands}`, 10, 44);
  ctx.fillText(`Level ${g.level}`, 10, 64);
}

export default function ParaTrooperPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [game, setGame] = useState<ParatrooperGame>(() => createGame(1));
  const [gameOver, setGameOver] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const shootingRef = useRef(false);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const { w, h, groundY, cannonY } = getCanvasSize();

  gameRef.current = game;

  function resetGame() {
    const g = createGame(1);
    setGame(g);
    gameRef.current = g;
    setGameOver(false);
  }

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const g = gameRef.current;
      if (g.gameOver) return;
      if (e.key === "ArrowLeft") setGame((prev) => setCannonX(prev, prev.cannonX - 12));
      if (e.key === "ArrowRight") setGame((prev) => setCannonX(prev, prev.cannonX + 12));
      if (e.key === " " || e.key === "Space") {
        e.preventDefault();
        shootingRef.current = true;
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => { window.removeEventListener("keydown", handleKey); };
  }, []);

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const scaleX = w / rect.width;
    setGame((prev) => setCannonX(prev, x * scaleX));
  }

  useEffect(() => {
    if (game.gameOver) { setGameOver(true); return; }
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    draw(ctx, game, groundY, cannonY);

    return;
  }, [game, groundY, cannonY]);

  useEffect(() => {
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      const shouldShoot = shootingRef.current;
      shootingRef.current = false;
      if (dt < 100) {
        setGame((prev) => tick(prev, dt, shouldShoot));
      }
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="game-shell">
      {!hideTitle && <h1 className="title">ParaTrooper</h1>}

      <section className="board-panel">
        <p className="status">{gameOver ? "Game Over!" : `Score: ${game.score}`}</p>
        <canvas
          ref={canvasRef}
          width={w}
          height={h}
          className="paratrooper-canvas"
          onMouseMove={handleMouseMove}
          onClick={() => { if (!game.gameOver) shootingRef.current = true; }}
        />
        {gameOver && (
          <div className="actions" style={{ justifyContent: "center", marginTop: 12 }}>
            <button onClick={resetGame}>Play Again</button>
          </div>
        )}
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Move mouse to aim cannon. Click or press Space to fire.</p>
          <p>2. Shoot paratroopers before they land. Helicopters drop bombs!</p>
          <p>3. If {game.maxLands} troopers land, game over!</p>
        </div>
      </section>
    </div>
  );
}
